import CLIENT from '../utils/restClient';
import Logger, {ALL_MESSAGES, LOG_LEVELS} from '../utils/logger';
import asyncjs from 'async';
import _ from 'underscore';
import { ACTION_TYPES as FILE_ACTIONS } from './files';

export const MODULE_NAME = 'DOCUMENTS_CONTROLLER';
const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
  return MODULE_NAME+'.'+name;
}

const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_STOP = namespaced('LOAD_STOP');
const LOAD_FAIL = namespaced('LOAD_FAIL');
const LOAD_RDI_TENANT_ID_OK = namespaced('LOAD_RDI_TENANT_ID_OK');
const LOAD_AUI_AT_OK = namespaced('LOAD_AUI_AT_OK');
const ACTION = namespaced('ACTION');
const ACTION_PROGRESS = namespaced('ACTION_PROGRESS');
const ACTION_OK = namespaced('ACTION_OK');
const ACTION_FAIL = namespaced('ACTION_FAIL');
const LOAD_DETAILS  = namespaced('LOAD_DETAILS');
const LOAD_DETAILS_OK  = namespaced('LOAD_DETAILS_OK');
const LOAD_DETAILS_FAIL  = namespaced('LOAD_DETAILS_FAIL');
const LOAD_STATUS  = namespaced('LOAD_STATUS');
const LOAD_STATUS_OK  = namespaced('LOAD_STATUS_OK');
const LOAD_STATUS_FAIL  = namespaced('LOAD_STATUS_FAIL');
const UNLOAD_DETAILS = namespaced('UNLOAD_DETAILS');

export const ACTION_TYPES = {
  LOAD: LOAD,
  LOAD_OK: LOAD_OK,
  LOAD_STOP: LOAD_STOP,
  LOAD_FAIL: LOAD_FAIL,
  LOAD_RDI_TENANT_ID_OK: LOAD_RDI_TENANT_ID_OK,
  LOAD_AUI_AT_OK: LOAD_AUI_AT_OK,
  ACTION: ACTION,
  ACTION_PROGRESS: ACTION_PROGRESS,
  ACTION_OK: ACTION_OK,
  ACTION_FAIL: ACTION_FAIL,
    LOAD_DETAILS: LOAD_DETAILS,
    LOAD_DETAILS_OK: LOAD_DETAILS_OK,
    LOAD_DETAILS_FAIL: LOAD_DETAILS_FAIL,
    UNLOAD_DETAILS:UNLOAD_DETAILS,
    LOAD_STATUS: LOAD_STATUS,
    LOAD_STATUS_OK: LOAD_STATUS_OK,
    LOAD_STATUS_FAIL: LOAD_STATUS_FAIL,
};

function getFileProcessedStatusFromUploadStatus(status) {
  switch (status) {
    case FILE_ACTIONS.FILE_UPLOAD:
      return 'Uploading...';
    case FILE_ACTIONS.FILE_UPLOAD_OK:
      return 'Queued...';
    case FILE_ACTIONS.FILE_UPLOAD_FAIL:
      return 'Failed to upload...';
    default:
      return 'Unknown...'
  }
}

/**
 * Cleans up and prepares the file list to show correct status
 * 
 * @param {*} uploadedFiles: Files uploaded  
 * @param {*} processedFiles: Files being processed and their status 
 */
function scrubUploadedFiles(uploadedFiles, processedFiles) {
  log.debug('scrubUploadedFiles: ', uploadedFiles, processedFiles );
  uploadedFiles = uploadedFiles || [];
  processedFiles = processedFiles || [];

  //Returns true if the given uploaded file is in process; otherwise false
  const isBeingProcessed = ( uploadedFilename ) => {
    return processedFiles.some( (file) =>{
       return file.fileName.includes(uploadedFilename);
    });
  };

  //Clean up the uploaded files to show correct status
  const filteredUploadedFiles = uploadedFiles.filter( (uploadedFile) => {
    //Check if a filename has extension
    const lastIndexOfPeriod = uploadedFile.fileName.lastIndexOf('.');
    //Remove files without extensions; as files without extensions are not supported
    if(lastIndexOfPeriod < 0) {
      return false;
    }
    //All file extensions are eventually converted to xml, we need to match with filename without extension 
    const uploadFileNameWithoutExtension = uploadedFile.fileName.substring(0, lastIndexOfPeriod);
    const isFileInProcess = isBeingProcessed(uploadFileNameWithoutExtension);
    //Filter out files under process or failed to upload 
    return !isFileInProcess && uploadedFile.processingDate !== 'Failed to upload...';
  });

  //Return the filtered list of files to be shown
  return filteredUploadedFiles;
}

function addToUploadedFiles(uploadedFiles, processedFiles, file, status) {
  log.debug('addToUploadedFiles: ', uploadedFiles, file, status );

  uploadedFiles = uploadedFiles || [];
  processedFiles = processedFiles || [];
  if (!file) {
    return scrubUploadedFiles(uploadedFiles, processedFiles);
  }
  // Remove a previous entry from the uploadedFiles if it has the same name as the new one
  uploadedFiles = uploadedFiles.filter((uploadedFile) => {return uploadedFile.fileName !== file.name});
  // Add (/replace) the new file, making the structure look the same as for a processed document for easy merging
  uploadedFiles.push({id: null, fileName: file.name, processingDate: status, processedCount: 0, errorCount: 0});

  //If error due to duplicate file, do not remove duplicate file from unprocessed file section
  if(file.error !== undefined && file.error.status === 409)
  {
    return uploadedFiles;
  }

  return scrubUploadedFiles(uploadedFiles, processedFiles);
}

export const updateSubStateFromAction = (state = {}, action) => {
  log.debug('updateSubStateFromAction: ', action, state );
  const getNewState = (action, state) => {
    switch (action.type) {
      case LOAD:
        return Object.assign({}, state, {loading: true, error: null});
      case LOAD_RDI_TENANT_ID_OK:
        return Object.assign({}, state, {rdiTenantId: action.body?.tenantId, loading: false, error: null});
      case LOAD_AUI_AT_OK:
        return Object.assign({}, state, {auiAt: action.response, loading: false, error: null});
      case LOAD_OK:
        return Object.assign({}, state, {processedDocuments: action.response, loading: false, error: null, uploadedFiles: scrubUploadedFiles(state.uploadedFiles, action.response)});
      case LOAD_FAIL:
        return Object.assign({}, state, {processedDocuments: undefined, loading: false, error: action.error});
      case LOAD_STOP:
        return Object.assign({}, state, { loading: false });
        case LOAD_STATUS:
            return Object.assign({}, state, {error: null});
        case LOAD_STATUS_OK:
            return Object.assign({}, state, {error: null});
        case LOAD_STATUS_FAIL:
            return Object.assign({}, state, {error: action.error});
        case LOAD_DETAILS:
            return Object.assign({}, state, {loading: true, error: null});
        case LOAD_DETAILS_OK:
            return Object.assign({}, state, {documentDetails: action.response, loading: false, error: null});
        case LOAD_DETAILS_FAIL:
            return Object.assign({}, state, {documentDetails: undefined, loading: false, error: action.error});
        case UNLOAD_DETAILS:
            return Object.assign({}, state, {documentDetails: undefined, loading: false, error: null});

      case ACTION:
        return  Object.assign({}, state, {loading: true, error: null, progress: action.progress});
      case ACTION_PROGRESS:
        return  Object.assign({}, state, {error: null, progress: action.progress});
      case ACTION_OK:
        return Object.assign({}, state, {loading: false, error: null});
      case ACTION_FAIL:
        return Object.assign({}, state, {loading: false, failures: action.failures});
      // Reacting to File Upload actions to populate the queued files list within the processedDocuments sub-state
      case FILE_ACTIONS.FILE_UPLOAD:
      case FILE_ACTIONS.FILE_UPLOAD_OK:
      case FILE_ACTIONS.FILE_UPLOAD_FAIL:
        return Object.assign({}, state, {uploadedFiles: addToUploadedFiles(state.uploadedFiles, state.processedDocuments, action.status, getFileProcessedStatusFromUploadStatus(action.type))});
      default:
        return state;
    }
  };
  const newState = getNewState(action, state);
  log.debug('updateSubStateFromAction:  newState', newState );
  return newState;
};


function getClientFnFromAction(action) {
  function unknownDocumentAction() {
    const args = arguments;
    // Do this as a promise, so it behaves exactly like the CLIENT actions
    return new Promise((resolve, reject) => {
      log.error('unknownDocumentActionFactory: ', action, args);
      reject(MODULE_NAME+': Unknown document action: ' + action);
    });
  }

  switch (action) {
    case 'delete':
      return CLIENT.deleteProcessedDocument.bind(CLIENT);
    case 'redeliver':
      return CLIENT.redeliverProcessedDocument.bind(CLIENT);
    default:
      return unknownDocumentAction;
  }
}

export const API = {
  doLoad: (apiParams={}) => {
    return (dispatch, getState) => {
      log.debug('doLoad: ');
      dispatch({type: LOAD});
      let fetchRequestPromise = null;
      if(`${apiParams.manualDocumentsOnly}` === 'true') {
        fetchRequestPromise = CLIENT.getManualDocuments(apiParams);
      } else {
        fetchRequestPromise = CLIENT.getProcessedDocuments(apiParams);
      }
      return fetchRequestPromise.then(response => {
            dispatch({type: LOAD_OK, response});
        })
        .catch(error => {
            log.debug('doLoad: error',error);
            dispatch({type: LOAD_FAIL, error});
        });
    };
  },
  doLoadV2: (apiParams={}) => {
    return (dispatch, getState) => {
      log.debug('doLoadV2: ');
      dispatch({type: LOAD});
      let fetchRequestPromise = null;
      if(`${apiParams.manualDocumentsOnly}` === 'true') {
        fetchRequestPromise = CLIENT.getManualDocumentsV2(apiParams);
      } else {
        fetchRequestPromise = CLIENT.getProcessedDocumentsV2(apiParams);
      }
      return fetchRequestPromise.then(response => {
            dispatch({type: LOAD_OK, response});
        })
        .catch(error => {
            log.debug('doLoadV2: error',error);
            dispatch({type: LOAD_FAIL, error});
        });
    };
  },

  doLoadRdiTenantId: () => {
    return async (dispatch) => {
      try {
        const response = await CLIENT.updateRdiTenantId();
        try {
          const body = await response.json();
          dispatch({ type: LOAD_RDI_TENANT_ID_OK, body });
        }
        catch (error) {
          dispatch({ type: LOAD_STOP });
        }
      }
      catch (error) {
        dispatch({ type: LOAD_STOP });
      }
    };
  },

  doLoadAuiAt: () => {
    return async (dispatch) => {
      try {
        const response = await CLIENT.getAccessToken();
        dispatch({ type: LOAD_AUI_AT_OK, response });
      }
      catch (error) {
        dispatch({ type: LOAD_STOP, error });
      }
    }
  },

    doLoadDocumentDetails: (documentId) => {
        return (dispatch, getState) => {
            log.debug('doLoadDocumentDetails: ',documentId);
            dispatch({type: LOAD_DETAILS});
            return CLIENT.getProcessedDocumentDetails(documentId)
                .then(response => {
                      log.debug('doLoadDocumentDetails: ok',response);

                    dispatch({type: LOAD_DETAILS_OK, response});
                })
                .catch(error => {
                    log.debug('doLoadDocumentDetails: ok',error);

                    dispatch({type: LOAD_DETAILS_FAIL, error});
                });
        }
    },
    doGetProcessedFileStatus: (fileId,cb) => {
        return (dispatch, getState) => {
            log.debug('doGetProcessedFileStatus: ',fileId);
            dispatch({type: LOAD_STATUS});
            return CLIENT.getProcessedFileStatus(fileId)
                .then(response => {

                    cb(response.id,response.status,response.fileName,response.error);
                    dispatch({type: LOAD_STATUS_OK, response});
                })
                .catch(error => {
                    log.debug('doGetProcessedFileStatus: error',error);
                    cb(fileId,'Failed','','');
                    dispatch({type: LOAD_STATUS_FAIL, error});
                });
        }
    },
    doUnLoadDocumentDetails: () => {
        return (dispatch, getState) => {
            log.debug('doUnLoadDocumentDetails: ');
            dispatch({type: UNLOAD_DETAILS});

        };
    },

  doActionOnDocuments: (action, documentIds, cb) => {
    cb = typeof cb === 'function' ? cb : () => {};
    documentIds = _.isArray(documentIds) ? documentIds : [documentIds];
    let progressTracker = {total: documentIds.length, count: 0, successes: [], failures: []};
    log.clearDictionaryError(ALL_MESSAGES.FILE_DELETED_ERROR_FAILED_PROCESSING);
    log.clearDictionaryError(ALL_MESSAGES.FILE_DELETED_ERROR_STILL_PROCESSING);
    log.clearDictionaryError(ALL_MESSAGES.FILE_DELETED_ERROR_NOT_FOUND);
    log.clearDictionaryError(ALL_MESSAGES.FILE_DELETED_ERROR); 
    log.clearDictionaryError(ALL_MESSAGES.FILE_DELETED_UNHANDLED_ERROR);
    log.clearDictionaryError(ALL_MESSAGES.FILE_DELETED_ERROR_ID_LIST); 
    return (dispatch, getState) => {
      log.debug('doActionOnDocuments: "%s": ', action, documentIds);
      const clientAction = getClientFnFromAction(action);
      dispatch({type: ACTION, progress: progressTracker});
      asyncjs.forEachOfSeries(documentIds, (documentId, idx, done) => {
        clientAction(documentId)
          .then(response => {
            log.debug('doActionOnDocuments: "%s": documentId: %s): succeeded: ', action, documentId, response);
            progressTracker.successes.push({id: documentId.fileName, response: response});
            progressTracker.count++;
            dispatch({type: ACTION_PROGRESS, progress: progressTracker});
            if(action === 'delete') {
              if(response !== undefined) {
                log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_SUCCESSFULLY);
              }  else  {               
                log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_UNHANDLED_ERROR);
                progressTracker.failures.push({id: documentId.fileName, message: ''});
              }
            } 
            if(action === 'redeliver') {
              log.setDictionaryMessage(ALL_MESSAGES.PROCESSED_FILE_REDELIVER_SENT_SUCCESS);
            }
            if (progressTracker.failures.length <= 0) {
              log.debug('doActionOnDocuments: "%s": succeeded: ', action);
              cb(null, progressTracker.successes);
            } else {
              cb(progressTracker.failures, null);
            }
          })
          .catch(error => {
            log.debug('doActionOnDocuments: "%s": documentId: %s): failed: ', action, documentId, error);
            progressTracker.count++;
            dispatch({type: ACTION_PROGRESS, progress: progressTracker});
            if (action === 'delete') {
              // Check whether this has already been recorded as a failure before adding it to the progress tracker
              if (!progressTracker.failures.some(f => f.id === documentId.id))
                progressTracker.failures.push({fileName: documentId.fileName, id: documentId.id, message: 'failure'});

              // Check that we are dealing with a response object
              if (error.text) {
                error.text()
                  .then(errorMessage => {                   
                    if (error.status === 425) {
                      log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_ERROR_STILL_PROCESSING);
                    }
                    else if (error.status === 409) {
                      log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_ERROR);
                    }
                    else if (!error.ok) {                      
                      // displaying the UI messages as per errorMessage set in rest api
                      if (errorMessage.indexOf('File failed to process') !== -1) {
                        log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_ERROR_FAILED_PROCESSING);
                      } else if (errorMessage.indexOf('File was not found') !== -1) {
                        log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_ERROR_NOT_FOUND);
                      } else {
                        log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_UNHANDLED_ERROR);
                      }
                    }
                    else {
                        log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_UNHANDLED_ERROR);
                      }                    
                  });
              } else {
                log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_UNHANDLED_ERROR);
              }
            } else if (action === 'redeliver') {
              progressTracker.failures.push({id: documentId.fileName, message: ''});
              log.setDictionaryMessage(ALL_MESSAGES.VAT_CODES_REDELIVER_ERROR);
            }
            log.debug('doActionOnDocuments: "%s":: failed: ', action, progressTracker);
            cb(progressTracker.failures);
            dispatch({type: ACTION_FAIL, error: progressTracker.failures.length + ' / ' + progressTracker.total + ' requests failed', failures: progressTracker.failures});
          })
          .finally(done);
      }, () => {
        if(action === 'redeliver') {
          log.setDictionaryMessage(ALL_MESSAGES.PROCESSED_FILE_REDELIVER_SENT_SUCCESS);
          return dispatch({type: ACTION_OK, redeliveredDocuments: documentIds});
        } else if(action === 'delete') {
          let failures = progressTracker.failures;
          if (failures.length > 1) {
            log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_ERROR_ID_LIST, failures.length, failures.map(f => f.fileName).join(', '));
          }
          let successes = progressTracker.successes;
          if (successes.length > 0) {
            log.setDictionaryMessage(ALL_MESSAGES.FILE_DELETED_SUCCESSFULLY, successes.length);
          }

          return dispatch({type: ACTION_OK, deletedDocuments: documentIds});
        }
      });
    };
  },

  doActionOnDocumentsV2: (values, cb) => {
    cb = typeof cb === 'function' ? cb : () => { };
    log.clearDictionaryError(ALL_MESSAGES.FILE_OPERATION_STARTED_SUCCESS);
    return (dispatch, getState) => {
      log.debug('doActionOnDocumentsV2: "%s": ', values.action, values.documentIds);

      return CLIENT.doActionOnProcessedDocuments(values)
        .then(response => {
          log.debug('doActionOnDocumentsV2: "%s": documentId: %s): succeeded: ', values.action, values.documentIds, response);
          cb(null, response);
          dispatch({type: ACTION_OK, processedDocuments: values.documentIds});
        })
        .catch(error => {
          log.debug('doActionOnDocuments: "%s": documentId: %s): failed: ', values.action, values.documentIds, error);
          cb(error);
          dispatch({type: ACTION_FAIL, error});
        })
    };
  },
};

export default API;
